import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Info, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * One toast at a time, bottom-right, 4s, with undo where undo is possible
 * (§10.6). Verb agreement is mandatory: the button says `Nashr etish`, the
 * toast says `Nashr etildi`.
 *
 * aria-live so a screen reader hears the confirmation too (§10.9).
 */

type Tone = 'success' | 'error' | 'info';

interface ToastPayload {
  message: string;
  tone?: Tone;
  undo?: { label: string; run: () => void };
  detail?: string;
}

interface ToastState extends ToastPayload {
  id: number;
}

const ToastContext = createContext<((payload: ToastPayload) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const counter = useRef(0);

  const show = useCallback((payload: ToastPayload) => {
    window.clearTimeout(timer.current);
    counter.current += 1;
    setToast({ ...payload, id: counter.current });
    // Errors stay longer: a message you cannot read is not a message.
    timer.current = window.setTimeout(() => setToast(null), payload.tone === 'error' ? 7000 : 4000);
  }, []);

  const dismiss = useCallback(() => {
    window.clearTimeout(timer.current);
    setToast(null);
  }, []);

  const value = useMemo(() => show, [show]);

  const icons: Record<Tone, ReactNode> = {
    success: <Check className="h-4 w-4" />,
    error: <AlertTriangle className="h-4 w-4" />,
    info: <Info className="h-4 w-4" />,
  };

  // Green appears exactly once in this application: here. Not as a palette.
  const tones: Record<Tone, string> = {
    success: 'border-grass-400/40 text-grass-500 dark:text-grass-400',
    error: 'border-accent-500/40 text-accent-600 dark:text-accent-400',
    info: 'border-line text-brand-500 dark:text-brand-300',
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-6 right-6 z-[60] flex w-[min(24rem,calc(100vw-3rem))] flex-col items-end"
      >
        {/*
          Slide up 12px + fade, 160ms in (§10.7). CSS, not Framer: this
          component mounts on every screen, and Framer's 38 KB on first paint
          buys nothing a keyframe cannot do.
        */}
        {toast && (
            <div
              key={toast.id}
              className={cn(
                'pointer-events-auto flex w-full animate-toast-in items-start gap-3 rounded-card border bg-surface px-4 py-3 shadow-pop',
                tones[toast.tone ?? 'info'],
              )}
            >
              <span className="mt-0.5 shrink-0">{icons[toast.tone ?? 'info']}</span>
              <div className="min-w-0 flex-1">
                <p className="text-ui font-medium text-strong">{toast.message}</p>
                {toast.detail && <p className="mt-0.5 text-ui leading-snug text-muted">{toast.detail}</p>}
              </div>
              {toast.undo && (
                <button
                  type="button"
                  onClick={() => {
                    toast.undo?.run();
                    dismiss();
                  }}
                  className="shrink-0 cursor-pointer rounded-control px-2 py-1 text-ui font-medium text-brand-500 transition-ui duration-instant hover:bg-raised dark:text-brand-300"
                >
                  <Undo2 aria-hidden className="mr-1 inline h-3.5 w-3.5" />
                  {toast.undo.label}
                </button>
              )}
              <button
                type="button"
                onClick={dismiss}
                aria-label="Yopish"
                className="shrink-0 cursor-pointer rounded-control p-1 text-faint transition-ui duration-instant hover:text-strong"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (payload: ToastPayload) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
